import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';

export type B3DsInputSelectBackground = 'white' | 'grey';

type B3DsInputSelectElement = HTMLElement & {
  value?: string;
  label?: string;
  placeholder?: string;
  hint?: string;
  error?: boolean;
  disabled?: boolean;
  background?: B3DsInputSelectBackground;
  maxHeight?: string;
};

@Directive({
  standalone: false,
  selector: 'b3-input-select[formControlName], b3-input-select[ngModel]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => B3DsInputSelectDirective),
      multi: true,
    },
  ],
})
export class B3DsInputSelectDirective implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() error = false;
  @Input() background: B3DsInputSelectBackground = 'white';
  @Input() inputId = '';
  @Input() maxHeight = '280px';
  @Input() disabled = false;
  @Input() options: B3SelectItem[] = [];

  private onChange: (value: B3SelectItem | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private selectedOption: B3SelectItem | null = null;
  private formDisabled = false;
  private optionsSyncTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly elementRef: ElementRef<B3DsInputSelectElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.syncElementState();
  }

  ngOnDestroy(): void {
    if (this.optionsSyncTimer != null) {
      clearTimeout(this.optionsSyncTimer);
      this.optionsSyncTimer = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && this.options.length) {
      this.rematchSelectedOptionFromOptions();
    }

    if (
      changes['label'] ||
      changes['placeholder'] ||
      changes['hint'] ||
      changes['error'] ||
      changes['background'] ||
      changes['inputId'] ||
      changes['maxHeight'] ||
      changes['disabled'] ||
      changes['options']
    ) {
      if (changes['options']) {
        // Projected <b3-input-select-option> update after this hook; re-apply value next tick.
        this.scheduleOptionsValueRefresh();
      } else {
        this.syncElementState();
      }
    }
  }

  writeValue(value: B3SelectItem | string | number | null): void {
    if (value == null || value === '') {
      this.selectedOption = null;
    } else if (typeof value === 'object') {
      const rawId = (value as B3SelectItem).inputId;
      this.selectedOption = this.findOption(rawId != null ? String(rawId) : undefined)
        ?? (value as B3SelectItem);
    } else if (typeof value === 'string' && value.trim()) {
      this.selectedOption = this.findOption(value) ?? null;
    } else {
      this.selectedOption = this.findOption(String(value)) ?? null;
    }
    this.syncElementState();
  }

  registerOnChange(fn: (value: B3SelectItem | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.syncElementState();
  }

  @HostListener('b3-change', ['$event'])
  onB3Change(event: Event): void {
    const customEvent = event as CustomEvent<{ value?: string | string[] }>;
    const rawValue = customEvent.detail?.value;
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;

    this.selectedOption = this.findOption(value) ?? null;
    this.syncElementState();
    this.onChange(this.selectedOption);
    this.onTouched();
  }

  @HostListener('b3-blur')
  onB3Blur(): void {
    this.onTouched();
  }

  private findOption(value: string | undefined): B3SelectItem | undefined {
    if (value == null || value === '') {
      return undefined;
    }

    const normalizedValue = this.normalizeSelectText(value);
    return this.options.find(option => {
      if (option.inputId != null && String(option.inputId) === String(value)) {
        return true;
      }

      return this.normalizeSelectText(String(option.label ?? '')) === normalizedValue
        || this.normalizeSelectText(String(option.data ?? '')) === normalizedValue;
    });
  }

  private normalizeSelectText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  }

  private rematchSelectedOptionFromOptions(): void {
    const currentValue = this.selectedOption?.inputId ?? this.elementRef.nativeElement.value;
    const currentLabel = this.selectedOption?.label ?? this.selectedOption?.data;
    if ((currentValue == null || currentValue === '') && !currentLabel) {
      return;
    }

    this.selectedOption =
      this.findOption(currentValue != null ? String(currentValue) : undefined)
      ?? this.findOption(currentLabel != null ? String(currentLabel) : undefined)
      ?? this.selectedOption;
  }

  private scheduleOptionsValueRefresh(): void {
    if (this.optionsSyncTimer != null) {
      clearTimeout(this.optionsSyncTimer);
    }

    this.optionsSyncTimer = setTimeout(() => {
      this.optionsSyncTimer = null;
      this.rematchSelectedOptionFromOptions();
      this.syncElementState(true);
    }, 0);
  }

  private syncElementState(forceValueRefresh = false): void {
    const element = this.elementRef.nativeElement;
    const nextValue = this.selectedOption?.inputId != null ? String(this.selectedOption.inputId) : '';

    if (forceValueRefresh && nextValue) {
      element.value = '';
      this.renderer.setAttribute(element, 'value', '');
    }

    element.value = nextValue;
    element.disabled = this.disabled || this.formDisabled;
    element.error = this.error;
    element.background = this.background;
    element.maxHeight = this.maxHeight;

    this.setOptionalAttribute(element, 'label', this.label);
    this.setOptionalAttribute(element, 'placeholder', this.placeholder);
    this.setOptionalAttribute(element, 'hint', this.hint);
    this.setOptionalAttribute(element, 'background', this.background);
    this.setOptionalAttribute(element, 'max-height', this.maxHeight);
    this.setOptionalAttribute(element, 'value', element.value);

    if (this.inputId) {
      element.id = this.inputId;
      this.renderer.setAttribute(element, 'id', this.inputId);
    }
  }

  private setOptionalAttribute(element: B3DsInputSelectElement, name: string, value: string): void {
    if (value) {
      (element as unknown as Record<string, string>)[name] = value;
      this.renderer.setAttribute(element, name, value);
      return;
    }

    this.renderer.removeAttribute(element, name);
  }
}
